import React, { useState } from 'react';

interface CollapsibleSectionProps {
  /** Heading shown in the section's clickable bar. */
  title: string;
  /** Optional muted badge to the right of the title — usually a count
   *  ("(6)") or a status hint. */
  badge?: React.ReactNode;
  /** Initial open state. Most editor sections default to open so the
   *  workspace is usable at a glance; the user collapses to save
   *  vertical space. */
  defaultOpen?: boolean;
  /** Optional className appended to the outer section so callers can
   *  target their specific block (e.g. add a wider min-height for the
   *  timeline). */
  className?: string;
  /** When the wrapped child already has its own background / padding
   *  (e.g. .timeline-wrap, .aspect-rail), set this to drop the
   *  body's chrome so the inner element fills the section edge-to-
   *  edge. */
  flush?: boolean;
  /** Callback ref for an actions container rendered at the right end
   *  of the header row (outside the toggle button, so interactive
   *  children don't fight the collapse click). Callers portal their
   *  controls into the provided element. */
  onActionsHost?: (el: HTMLElement | null) => void;
  children: React.ReactNode;
}

/**
 * Shared collapsible section wrapper. Reuses the existing
 * `.assets-panel*` styles so every collapsible block in the editor
 * (Project Assets, Timeline, Aspect Ratios, …) looks and behaves the
 * same: heading bar with a rotating chevron, body that mounts only
 * when open. Body is unmounted when collapsed so heavy children
 * (Timeline, AspectRail) don't pay render cost while hidden.
 */
export default function CollapsibleSection({
  title, badge, defaultOpen = true, className, flush, onActionsHost, children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const cls = ['assets-panel', open ? 'is-open' : '', className || '']
    .filter(Boolean).join(' ');
  const bodyCls = ['assets-panel-body', flush ? 'assets-panel-body--flush' : '']
    .filter(Boolean).join(' ');
  return (
    <section className={cls}>
      <div className="assets-panel-headrow">
        <button
          type="button"
          className="assets-panel-head"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <span className={`assets-panel-chevron${open ? ' is-open' : ''}`} aria-hidden>▸</span>
          <span className="assets-panel-title">{title}</span>
          {badge !== undefined && <span className="muted">{badge}</span>}
        </button>
        {onActionsHost && (
          <span
            className="assets-panel-actions"
            ref={onActionsHost}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>
      {open && <div className={bodyCls}>{children}</div>}
    </section>
  );
}
